import * as React from 'react';
import { useLocation } from 'wouter';
import Fuse from 'fuse.js';
import {
  CommandDialog,
  CommandEmpty,
  CommandInput,
  CommandList,
} from '../../components/ui/command';
import { useGlobalSearch } from './hooks';
import type { GlobalSearchTask } from './types';
import { SearchResultGroups } from './SearchResultGroups';
import { 
  taskFuseOptions, 
  projectFuseOptions, 
  categoryFuseOptions, 
  contextFuseOptions 
} from './searchConfig';

export function GlobalCommandSearch() {
  const { isOpen, search, setSearch, openSearch, closeSearch, searchResults } = useGlobalSearch();
  const [, navigate] = useLocation();

  // Create Fuse instances
  const activeFuse = React.useMemo(() => new Fuse(searchResults.activeTasks, taskFuseOptions), [searchResults.activeTasks]);
  const completedFuse = React.useMemo(() => new Fuse(searchResults.completedTasks, taskFuseOptions), [searchResults.completedTasks]);
  const projectFuse = React.useMemo(() => new Fuse(searchResults.projects, projectFuseOptions), [searchResults.projects]);
  const categoryFuse = React.useMemo(() => new Fuse(searchResults.categories, categoryFuseOptions), [searchResults.categories]);
  const contextFuse = React.useMemo(() => new Fuse(searchResults.contexts, contextFuseOptions), [searchResults.contexts]);

  // Perform fuzzy search
  const filteredResults = React.useMemo(() => {
    if (!search.trim()) {
      return {
        activeTasks: searchResults.activeTasks.slice(0, 10), // Limit for performance
        completedTasks: searchResults.completedTasks.slice(0, 5),
        projects: searchResults.projects.slice(0, 5),
        categories: searchResults.categories.slice(0, 5),
        contexts: searchResults.contexts.slice(0, 5),
      };
    }

    return {
      activeTasks: activeFuse.search(search).map(result => result.item).slice(0, 10),
      completedTasks: completedFuse.search(search).map(result => result.item).slice(0, 5),
      projects: projectFuse.search(search).map(result => result.item).slice(0, 5),
      categories: categoryFuse.search(search).map(result => result.item).slice(0, 5),
      contexts: contextFuse.search(search).map(result => result.item).slice(0, 5),
    };
  }, [search, activeFuse, completedFuse, projectFuse, categoryFuse, contextFuse, searchResults]);

  // Handle keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || 
          (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA')) {
        e.preventDefault();
        openSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openSearch]);

  const handleSelectTask = (task: GlobalSearchTask) => {
    closeSearch();
    // Navigate to the task in its context
    navigate(`/context/${task.contextId}/task/${task._id}`);
  };

  const handleSelectProject = (project: typeof searchResults.projects[0]) => {
    closeSearch();
    navigate(`/context/${project.contextId}/project/${project._id}`);
  };

  const handleSelectCategory = (category: typeof searchResults.categories[0]) => {
    closeSearch();
    navigate(`/context/${category.contextId}/category/${category._id}`);
  };

  const handleSelectContext = (context: typeof searchResults.contexts[0]) => {
    closeSearch();
    navigate(`/context/${context._id}`);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      openSearch();
    } else {
      closeSearch();
    }
  };

  const hasResults = filteredResults.activeTasks.length > 0 || 
                   filteredResults.completedTasks.length > 0 || 
                   filteredResults.projects.length > 0 || 
                   filteredResults.categories.length > 0 || 
                   filteredResults.contexts.length > 0;

  return (
    <CommandDialog open={isOpen} onOpenChange={handleOpenChange}>
      <div className="sr-only" id="global-search-title">Global Search</div>
      <div className="sr-only" id="global-search-description">
        Search across all your tasks, projects, categories, and contexts. Use fuzzy search to find items by name or description.
      </div>
      <CommandInput 
        placeholder="Search tasks, projects, categories, contexts..." 
        value={search}
        onValueChange={setSearch}
        aria-describedby="global-search-description"
      />
      <CommandList filter={() => 1}>
        {!hasResults && (
          <CommandEmpty>No results found.</CommandEmpty>
        )}
        
        <SearchResultGroups
          filteredResults={filteredResults}
          onSelectTask={handleSelectTask}
          onSelectProject={handleSelectProject}
          onSelectCategory={handleSelectCategory}
          onSelectContext={handleSelectContext}
        />
      </CommandList>
    </CommandDialog>
  );
}